import { useCallback, useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHead, Stat, Empty, selectCls, qtyFmt } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { CircleDot, Loader2 } from "lucide-react";

const p2 = (n) => String(n).padStart(2, "0");
const curMonth = () => { const d = new Date(); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}`; };

export default function OwnerTireReport() {
  const [month, setMonth] = useState(curMonth());
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/erp/tire-sales", { params: { month } })
    .then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))), [month]);
  useEffect(() => { load(); }, [load]);
  const Table = ({ title, rows, keyField, testid }) => (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
      <div className="px-4 py-2 text-xs font-mono-caps text-zinc-500 uppercase">{title}</div>
      {rows.length === 0 ? <Empty>Sem vendas no mês.</Empty> : (
        <table className="w-full text-sm min-w-[480px]" data-testid={testid}>
          <thead><tr className="bg-zinc-900 text-[11px] font-mono-caps text-zinc-500 uppercase">
            <th className="text-left px-3 py-2">{keyField === "brand" ? "Marca" : "Medida"}</th>
            <th className="text-right px-3 py-2">Qtd.</th><th className="text-right px-3 py-2">Faturamento</th>
            <th className="text-right px-3 py-2">Custo</th><th className="text-right px-3 py-2">Lucro</th>
          </tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r[keyField]} data-testid={`${testid}-row-${r[keyField]}`} className="border-t border-zinc-800">
              <td className="px-3 py-2 text-zinc-100">{r[keyField]}</td>
              <td className="px-3 py-2 text-right text-zinc-300">{qtyFmt(r.qty)}</td>
              <td className="px-3 py-2 text-right text-zinc-100 tabular-nums">{brl(r.revenue)}</td>
              <td className="px-3 py-2 text-right text-red-300 tabular-nums">{brl(r.cost)}</td>
              <td className="px-3 py-2 text-right text-green-neon tabular-nums font-bold">{brl(r.profit)}</td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={CircleDot} kicker="Relatórios" title="Venda de pneus">
        <label className="text-xs text-zinc-400 inline-flex items-center gap-2">Mês
          <input data-testid="tires-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="h-10 rounded-md border border-zinc-800 bg-zinc-900 px-2 text-sm text-zinc-100" /></label>
      </PageHead>
      <p className="text-xs text-zinc-500 max-w-2xl">Somente peças com <strong>categoria "Pneus"</strong> no cadastro. Para aparecer, abra o estoque, edite a peça e informe categoria "Pneus", a marca e a medida (ex.: 185/65R15).</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="tires-qty" label="Pneus vendidos" value={data ? qtyFmt(data.qty) : "…"} />
        <Stat testid="tires-revenue" label="Faturamento" value={data ? brl(data.revenue) : "…"} tone="green" />
        <Stat testid="tires-cost" label="Custo total" value={data ? brl(data.cost) : "…"} tone="red" />
        <Stat testid="tires-profit" label="Lucro" value={data ? brl(data.profit) : "…"} tone={data?.profit < 0 ? "red" : "green"} />
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : (
        <>
          <Table title="Por marca" rows={data.by_brand} keyField="brand" testid="tires-brand" />
          <Table title="Por medida" rows={data.by_size} keyField="size" testid="tires-size" />
        </>
      )}
    </div>
  );
}
